import type { MagnusUIMessage } from "@magnus/contract/messages";
import type {
  SdkVoiceSessionSaved,
  SdkVoiceSessionSaveInput,
} from "@magnus/contract/voice";
import type { SdkRestResult } from "../../rest";

/** POSTs one recap; resolves the response rather than throwing on non-2xx. */
export type VoiceSessionPost = (
  chatId: string,
  body: SdkVoiceSessionSaveInput,
  keepalive: boolean
) => Promise<SdkRestResult<SdkVoiceSessionSaved>>;

export type VoiceSessionSaveOutcome = {
  message: MagnusUIMessage | null;
  discarded: boolean;
  /** False once every attempt failed and the recap is parked for later. */
  saved: boolean;
  /** The last failed status, when the save gave up. */
  status?: number;
};

const UNSAVED: VoiceSessionSaveOutcome = {
  message: null,
  discarded: false,
  saved: false,
};

/** Where the call's voice and captions prefs, and unsaved recaps waiting
 *  for the next load, live: `localStorage` on web, AsyncStorage on mobile.
 *  Each method may be sync or async. */
export type LiveCallStorage = {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
};

type SaveAttempt =
  | { ok: true; value: VoiceSessionSaveOutcome }
  | { ok: false; retry: boolean };

type ParkedVoiceSession = { chatId: string; body: SdkVoiceSessionSaveInput };

export const SAVE_RETRY_DELAYS_MS = [1000, 3000, 8000] as const;

/** Browsers cap a `keepalive` body at 64KB. */
export const KEEPALIVE_BODY_MAX_BYTES = 64_000;

const PARKED_INDEX_KEY = "magnus.voice.parked";
const parkedKey = (sessionId: string) => `${PARKED_INDEX_KEY}.${sessionId}`;

const encoder = new TextEncoder();

export function defaultLiveCallStorage(): LiveCallStorage | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * A failed status worth retrying: the server, the network, or a session
 * that has lapsed, not the request itself.
 */
export function isRetryableStatus(status: number): boolean {
  return status === 401 || status === 408 || status === 429 || status >= 500;
}

/**
 * Runs `attempt` until it succeeds or gives up. The save is idempotent
 * (the recap row is keyed `voice-<sessionId>` and inserted on conflict do
 * nothing), so a retry after an ambiguous failure cannot duplicate it.
 */
export async function saveWithRetry(
  attempt: () => Promise<SaveAttempt>,
  delays: readonly number[] = SAVE_RETRY_DELAYS_MS
): Promise<VoiceSessionSaveOutcome | null> {
  for (let i = 0; ; i++) {
    const result = await attempt();
    if (result.ok) {
      return result.value;
    }
    const delay = delays[i];
    if (!result.retry || delay === undefined) {
      return null;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
}

/**
 * The body a `keepalive` unload save may carry. Drops the oldest turns
 * first, so the recap still records the end of the call.
 */
export function trimSaveBody(
  body: SdkVoiceSessionSaveInput,
  maxBytes: number = KEEPALIVE_BODY_MAX_BYTES
): SdkVoiceSessionSaveInput {
  let turns = body.turns;
  while (encoder.encode(JSON.stringify({ ...body, turns })).length > maxBytes) {
    if (turns.length === 0) {
      return { ...body, turns };
    }
    turns = turns.slice(1);
  }
  return turns === body.turns ? body : { ...body, turns };
}

async function readIndex(storage: LiveCallStorage): Promise<string[]> {
  try {
    const raw = await storage.getItem(PARKED_INDEX_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

/** Keeps a recap the server could not take, for `flushVoiceSessions`. */
export async function parkVoiceSession(
  storage: LiveCallStorage,
  parked: ParkedVoiceSession
): Promise<void> {
  try {
    const ids = await readIndex(storage);
    const id = parked.body.sessionId;
    await storage.setItem(parkedKey(id), JSON.stringify(parked));
    if (!ids.includes(id)) {
      await storage.setItem(PARKED_INDEX_KEY, JSON.stringify([...ids, id]));
    }
  } catch {
    // storage unavailable; the recap is lost with the page
  }
}

/** Forgets a parked recap once it has been saved after all. */
export async function unparkVoiceSession(
  storage: LiveCallStorage,
  sessionId: string
): Promise<void> {
  try {
    const ids = await readIndex(storage);
    await storage.removeItem(parkedKey(sessionId));
    if (ids.includes(sessionId)) {
      await storage.setItem(
        PARKED_INDEX_KEY,
        JSON.stringify(ids.filter((id) => id !== sessionId))
      );
    }
  } catch {
    // storage unavailable; the next flush re-posts it, which is idempotent
  }
}

/** The server validates the body again on save; this only checks enough
 *  to post it. */
function isParked(value: unknown): value is ParkedVoiceSession {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { chatId, body } = value as Partial<ParkedVoiceSession>;
  return (
    typeof chatId === "string" &&
    typeof body === "object" &&
    body !== null &&
    typeof body.sessionId === "string" &&
    Array.isArray(body.turns)
  );
}

async function attemptSave(
  post: VoiceSessionPost,
  chatId: string,
  body: SdkVoiceSessionSaveInput,
  keepalive: boolean,
  onStatus: (status: number) => void
): Promise<SaveAttempt> {
  let res: SdkRestResult<SdkVoiceSessionSaved>;
  try {
    res = await post(chatId, body, keepalive);
  } catch {
    return { ok: false, retry: true };
  }
  if (!res.ok) {
    onStatus(res.status);
    return { ok: false, retry: isRetryableStatus(res.status) };
  }
  return {
    ok: true,
    value: {
      message: res.value?.message ?? null,
      discarded: res.value?.discarded === true,
      saved: true,
    },
  };
}

/**
 * Saves a recap, retrying transient failures. A recap that still won't save
 * is parked (in `localStorage` unless `storage` says otherwise) and sent by
 * `flushVoiceSessions` on the next load. During page unload (`keepalive`)
 * the recap is parked first, since the response may never arrive, trimmed
 * to the keepalive body cap, and unparked if the response does arrive.
 */
export async function saveVoiceSessionRecap(
  post: VoiceSessionPost,
  chatId: string,
  body: SdkVoiceSessionSaveInput,
  options: { keepalive?: boolean; storage?: LiveCallStorage } = {}
): Promise<VoiceSessionSaveOutcome> {
  const { keepalive = false } = options;
  const store = options.storage ?? defaultLiveCallStorage();
  let status: number | undefined;
  const onStatus = (s: number) => {
    status = s;
  };
  if (keepalive) {
    const trimmed = trimSaveBody(body);
    if (store) {
      await parkVoiceSession(store, { chatId, body: trimmed });
    }
    const result = await attemptSave(post, chatId, trimmed, true, onStatus);
    if (!result.ok) {
      return { ...UNSAVED, status };
    }
    if (store) {
      await unparkVoiceSession(store, body.sessionId);
    }
    return result.value;
  }
  const saved = await saveWithRetry(() =>
    attemptSave(post, chatId, body, false, onStatus)
  );
  if (saved) {
    return saved;
  }
  if (store && (status === undefined || isRetryableStatus(status))) {
    await parkVoiceSession(store, { chatId, body });
  }
  return { ...UNSAVED, status };
}

/**
 * Retries every recap parked by an earlier page, once. A save that
 * succeeds, or that the server rejects for good, is dropped; a transient
 * failure stays parked. Entries that no longer parse are dropped too.
 */
export async function flushVoiceSessions(
  post: VoiceSessionPost,
  storage?: LiveCallStorage
): Promise<void> {
  const store = storage ?? defaultLiveCallStorage();
  if (!store) {
    return;
  }
  const ids = await readIndex(store);
  if (ids.length === 0) {
    return;
  }
  const remaining: string[] = [];
  for (const id of ids) {
    let parked: unknown = null;
    try {
      const raw = await store.getItem(parkedKey(id));
      parked = raw ? JSON.parse(raw) : null;
    } catch {
      parked = null;
    }
    if (!isParked(parked)) {
      await store.removeItem(parkedKey(id));
      continue;
    }
    const result = await attemptSave(
      post,
      parked.chatId,
      parked.body,
      false,
      () => undefined
    );
    if (!result.ok && result.retry) {
      remaining.push(id);
      continue;
    }
    await store.removeItem(parkedKey(id));
  }
  await store.setItem(PARKED_INDEX_KEY, JSON.stringify(remaining));
}
